// pages/tasks/hooks/useTaskAutoSave.ts
import { useCallback, useRef } from "react";
import { Task } from "@/types";

interface UseTaskAutoSaveProps {
  newTaskRows: Partial<Task>[];
  createTask?: (task: Partial<Task>) => Promise<void>;
  fetchTasks: (filters: any) => void;
  filters: any;
  setNewTaskRows: (rows: Partial<Task>[]) => void;
}

export const useTaskAutoSave = ({
  newTaskRows,
  createTask,
  fetchTasks,
  filters,
  setNewTaskRows,
}: UseTaskAutoSaveProps) => {
  // Track which task is currently being saved to prevent duplicate saves
  const savingTasksRef = useRef<Set<string>>(new Set());

  const handleNewTaskBlur = useCallback(async (tempId: string) => {
    // Check if task has at least one required field filled
    const taskToSave = newTaskRows.find(row => row.tempId === tempId);
    
    if (taskToSave && (taskToSave.client || taskToSave.task || taskToSave.subTask)) {
      // Only save if at least one required field has value
      await saveNewTask(tempId);
    }
  }, [newTaskRows]);

  const saveNewTask = async (tempId: string) => {
    // Prevent duplicate saves
    if (savingTasksRef.current.has(tempId)) {
      return;
    }

    try {
      const taskRow = newTaskRows.find(row => row.tempId === tempId);
      if (!taskRow) return;

      // Mark as saving
      savingTasksRef.current.add(tempId);

      // Save the current focused element position
      const focusedElement = document.activeElement as HTMLElement;
      const focusedCell = focusedElement?.closest('[data-excel-cell]');
      const rowIndex = focusedCell?.getAttribute('data-row');
      const colIndex = focusedCell?.getAttribute('data-col');
      
      // Also save the current scroll position
      const scrollContainer = focusedCell?.closest('.overflow-auto');
      const scrollTop = scrollContainer?.scrollTop || 0;
      const scrollLeft = scrollContainer?.scrollLeft || 0;

      const { tempId: _, ...taskData } = taskRow;
      
      // Only create task if it has at least a client, task, or subTask
      if (taskData.client || taskData.task || taskData.subTask) {
        if (createTask) {
          await createTask({
            client: taskData.client || "",
            task: taskData.task || "",
            subTask: taskData.subTask || "",
            et: taskData.et || 0,
            allottedTo: taskData.allottedTo || "",
            teamLeader: taskData.teamLeader || "",
            priority: taskData.priority || "medium",
            dueDate: taskData.dueDate || new Date().toISOString(),
            frequency: taskData.frequency || "none",
            status: taskData.status || "todo",
            tt: taskData.tt || 0,
          });
          
          // Remove from new task rows after successful save
          setNewTaskRows(prev => prev.filter(row => row.tempId !== tempId));
          
          // Refresh tasks list
          await fetchTasks(filters);

          // Restore focus and scroll position after the DOM updates
          setTimeout(() => {
            // Restore scroll position first
            if (scrollContainer) {
              scrollContainer.scrollTop = scrollTop;
              scrollContainer.scrollLeft = scrollLeft;
            }

            // Then restore focus
            if (rowIndex !== null && colIndex !== null) {
              // Try to find the cell at the same position
              const newFocusTarget = document.querySelector(
                `[data-row="${rowIndex}"][data-col="${colIndex}"]`
              ) as HTMLElement;
              
              if (newFocusTarget) {
                newFocusTarget.focus();
              } else {
                // If the exact cell doesn't exist (because row was removed),
                // try to focus on the same column in the previous row
                const prevRowIndex = Math.max(0, parseInt(rowIndex) - 1);
                const fallbackTarget = document.querySelector(
                  `[data-row="${prevRowIndex}"][data-col="${colIndex}"]`
                ) as HTMLElement;
                fallbackTarget?.focus();
              }
            }
          }, 100);
        }
      }
    } catch (error) {
      console.error("Failed to save new task:", error);
    } finally {
      // Remove from saving set
      savingTasksRef.current.delete(tempId);
    }
  };

  return {
    handleNewTaskBlur,
  };
};